import { Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { diffFields, isTruncated } from './auditDiff';
import { AttributeTag } from '../../design-system/primitives/AttributeTag/AttributeTag';
import styles from './AuditEntryDetail.module.css';

/**
 * The expander behind spec D-6: an audit entry's `before_state` and
 * `after_state`, diffed field by field, old beside new. The diff logic
 * itself lives in `auditDiff.ts` — see that file for the union-of-keys,
 * null-coercion and `_bounds`-truncation rules this component renders.
 *
 * Renders `null` — nothing at all — when there is no diff to show: both
 * states are `null` (most audit rows carry no state), or both are recorded
 * but identical.
 */

export interface AuditEntryDetailProps {
  beforeState: Record<string, unknown> | null;
  afterState: Record<string, unknown> | null;
}

/** One side of one row: the field's raw value, or a placeholder distinguishing
 *  "this side never had the key" from "this side's value is `null`" — those
 *  are different facts and read differently. */
function FieldValue({ present, value }: { present: boolean; value: unknown }): ReactNode {
  const { t } = useTranslation();

  if (!present) {
    return <span className={styles['placeholder']}>{t('audit.detail.noField')}</span>;
  }
  if (value === null) {
    return <span className={styles['placeholder']}>{t('audit.detail.emptyValue')}</span>;
  }

  const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return <bdi dir="ltr">{text}</bdi>;
}

export function AuditEntryDetail({ beforeState, afterState }: AuditEntryDetailProps) {
  const { t } = useTranslation();

  if (isTruncated(beforeState) || isTruncated(afterState)) {
    return <p className={styles['truncated']}>{t('audit.detail.stateTruncated')}</p>;
  }

  const diffs = diffFields(beforeState, afterState);

  if (diffs.length === 0) return null;

  return (
    <dl className={styles['panel']}>
      {diffs.map((diff) => (
        <Fragment key={diff.key}>
          <dt className={styles['field']}>
            <bdi dir="ltr">{diff.key}</bdi>
            {!diff.hadBefore ? (
              <AttributeTag tone="accent" className={styles['tag']}>
                {t('audit.detail.added')}
              </AttributeTag>
            ) : null}
            {!diff.hadAfter ? (
              <AttributeTag tone="outline" className={styles['tag']}>
                {t('audit.detail.removed')}
              </AttributeTag>
            ) : null}
          </dt>
          <dd className={styles['values']}>
            <span>
              <span className={styles['srOnly']}>{t('audit.detail.before')}</span>
              <FieldValue present={diff.hadBefore} value={diff.before} />
            </span>
            {/* A neutral separator, not an arrow: an arrow is a directional
                glyph that would need an RTL mirror, and the two `srOnly`
                labels either side already carry "before"/"after" for anyone
                who cannot see the layout order. Same convention as the
                grouped-action list's own `·` separator (AuditEntryTable). */}
            <span className={styles['arrow']} aria-hidden="true">
              ·
            </span>
            <span>
              <span className={styles['srOnly']}>{t('audit.detail.after')}</span>
              <FieldValue present={diff.hadAfter} value={diff.after} />
            </span>
          </dd>
        </Fragment>
      ))}
    </dl>
  );
}
